import { supabase } from '../lib/supabase';
import * as dicomParser from 'dicom-parser';


// X-ray Machine Connection Service
export const xrayMachineService = {
  // Discover available X-ray machines on the network
  async discoverMachines() {
    try {
      // In production, this would use DICOM C-FIND to discover devices
      // For now, simulate network discovery
      const machines = [
        {
          id: 'xray-001',
          name: 'Planmeca ProMax 3D',
          type: 'Cone Beam CT',
          ipAddress: '192.168.1.100',
          port: 11112,
          aeTitle: 'PLANMECA_CT',
          manufacturer: 'Planmeca',
          model: 'ProMax 3D',
          status: 'online',
          capabilities: ['2D', '3D', 'Panoramic', 'Cephalometric']
        },
        {
          id: 'xray-002',
          name: 'Carestream CS 8100',
          type: 'Panoramic',
          ipAddress: '192.168.1.101',
          port: 11112,
          aeTitle: 'CARESTREAM_PAN',
          manufacturer: 'Carestream',
          model: 'CS 8100',
          status: 'online',
          capabilities: ['Panoramic', 'Bitewing']
        },
        {
          id: 'xray-003',
          name: 'Dentsply Sirona XIOS',
          type: 'Intraoral',
          ipAddress: '192.168.1.102',
          port: 11112,
          aeTitle: 'SIRONA_IO',
          manufacturer: 'Dentsply Sirona',
          model: 'XIOS Supreme',
          status: 'online',
          capabilities: ['Periapical', 'Bitewing']
        }
      ];

      return { success: true, machines };
    } catch (error) {
      console.error('Machine discovery error:', error);
      return { success: false, error: error?.message };
    }
  },

  // Connect to a specific X-ray machine
  async connectToMachine(machineId, config) {
    try {
      // In production, establish DICOM association with C-ECHO
      // Simulate connection
      await new Promise(resolve => setTimeout(resolve, 1000));

      const connection = {
        machineId,
        connected: true,
        timestamp: new Date()?.toISOString(),
        aeTitle: config?.aeTitle,
        status: 'ready'
      };

      // Store connection in Supabase
      const { data, error } = await supabase?.from('xray_machine_connections')?.insert({
          machine_id: machineId,
          ae_title: config?.aeTitle,
          ip_address: config?.ipAddress,
          port: config?.port,
          status: 'connected',
          connected_at: new Date()?.toISOString()
        })?.select()?.single();

      if (error) throw error;

      return { success: true, connection: data };
    } catch (error) {
      console.error('Connection error:', error);
      return { success: false, error: error?.message };
    }
  },

  // Disconnect from X-ray machine
  async disconnectFromMachine(machineId) {
    try {
      // In production, release DICOM association
      const { error } = await supabase?.from('xray_machine_connections')?.update({
          status: 'disconnected',
          disconnected_at: new Date()?.toISOString()
        })?.eq('machine_id', machineId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Disconnection error:', error);
      return { success: false, error: error?.message };
    }
  },

  // Acquire image from connected X-ray machine
  async acquireImage(machineId, acquisitionParams) {
    try {
      // In production, send DICOM C-STORE request to retrieve image
      // Simulate image acquisition
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Generate mock DICOM metadata
      const dicomMetadata = {
        studyInstanceUID: this.generateUID(),
        seriesInstanceUID: this.generateUID(),
        sopInstanceUID: this.generateUID(),
        patientName: acquisitionParams?.patientName || 'Unknown',
        patientId: acquisitionParams?.patientId || 'Unknown',
        studyDate: new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, ''),
        studyTime: new Date()?.toTimeString()?.split(' ')?.[0]?.replace(/:/g, ''),
        modality: acquisitionParams?.modality || 'DX',
        manufacturer: acquisitionParams?.manufacturer || 'Unknown',
        bodyPartExamined: acquisitionParams?.bodyPart || 'JAW',
        imageType: acquisitionParams?.imageType || 'ORIGINAL\\PRIMARY',
        kvp: acquisitionParams?.kvp || 70,
        exposure: acquisitionParams?.exposure || 8,
        rows: 2048,
        columns: 2048
      };

      // Store in Supabase
      const { data, error } = await supabase?.from('dental_images')?.insert({
          machine_id: machineId,
          patient_id: acquisitionParams?.patientId,
          image_type: acquisitionParams?.imageType || 'xray',
          category: acquisitionParams?.category || 'Periapical',
          tooth_number: acquisitionParams?.toothNumber,
          dicom_metadata: dicomMetadata,
          acquisition_date: new Date()?.toISOString(),
          status: 'acquired',
          provider_id: acquisitionParams?.providerId
        })?.select()?.single();

      if (error) throw error;

      return {
        success: true,
        image: {
          id: data?.id,
          machineId,
          dicomMetadata,
          acquisitionDate: data?.acquisition_date,
          status: 'acquired'
        }
      };
    } catch (error) {
      console.error('Image acquisition error:', error);
      return { success: false, error: error?.message };
    }
  },

  // Parse DICOM file
  async parseDicomFile(file) {
    try {
      const arrayBuffer = await file?.arrayBuffer();
      const byteArray = new Uint8Array(arrayBuffer);
      
      // Parse DICOM using dicom-parser
      const dataSet = dicomParser?.parseDicom(byteArray);
      
      // Extract key metadata
      const metadata = {
        patientName: dataSet?.string('x00100010') || 'Unknown',
        patientId: dataSet?.string('x00100020') || 'Unknown',
        studyDate: dataSet?.string('x00080020') || '',
        studyTime: dataSet?.string('x00080030') || '',
        modality: dataSet?.string('x00080060') || 'DX',
        manufacturer: dataSet?.string('x00080070') || 'Unknown',
        studyDescription: dataSet?.string('x00081030') || '',
        seriesDescription: dataSet?.string('x0008103e') || '',
        rows: dataSet?.uint16('x00280010'),
        columns: dataSet?.uint16('x00280011')
      };

      return { success: true, metadata, dataSet };
    } catch (error) {
      console.error('DICOM parsing error:', error);
      return { success: false, error: error?.message };
    }
  },

  // Get machine status
  async getMachineStatus(machineId) {
    try {
      // In production, send DICOM C-ECHO to verify connection
      const { data, error } = await supabase?.from('xray_machine_connections')?.select('*')?.eq('machine_id', machineId)?.order('connected_at', { ascending: false })?.limit(1)?.single();

      if (error) throw error;

      return {
        success: true,
        status: {
          machineId,
          connected: data?.status === 'connected',
          lastConnection: data?.connected_at,
          aeTitle: data?.ae_title
        }
      };
    } catch (error) {
      console.error('Status check error:', error);
      return { success: false, error: error?.message };
    }
  },

  // Generate DICOM UID
  generateUID() {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000);
    return `1.2.840.10008.5.1.4.1.1.${timestamp}.${random}`;
  },

  // Get acquisition history
  async getAcquisitionHistory(patientId) {
    try {
      const { data, error } = await supabase?.from('dental_images')?.select('*')?.eq('patient_id', patientId)?.order('acquisition_date', { ascending: false });

      if (error) throw error;

      return {
        success: true,
        images: data?.map(img => ({
          id: img?.id,
          machineId: img?.machine_id,
          imageType: img?.image_type,
          category: img?.category,
          toothNumber: img?.tooth_number,
          acquisitionDate: img?.acquisition_date,
          status: img?.status,
          dicomMetadata: img?.dicom_metadata
        }))
      };
    } catch (error) {
      console.error('History retrieval error:', error);
      return { success: false, error: error?.message };
    }
  }
};
